<template>
  <PageWrap page-title="Zonas">
    <LoadingIndicator v-if="loadingData" />

    <template v-else>
      <b-row>
        <b-col cols="5">
          <BfTable
            :data="zones"
            :columns="tableColumns"
            highlight-current-row
            @current-change="zoneTableSelectionChange"
                  cell-class-name="tasaciones-small-cell"

          >
            <template slot="nombre" slot-scope="{row}">
              <span
                class="tasaciones-table-marker"
                @click.stop="openEditZoneModal(row.id)"
              >
                {{row.nombre}}
              </span>
            </template>

            <template slot="delete" slot-scope="{row}">
              <i
                class="fa fa-trash-alt"
                @click.stop="deleteZone(row.id)"
              />
            </template>
          </BfTable>

          <div class="mt-4">
            <BfButton @click="createZoneClick">Añadir</BfButton>
          </div>
        </b-col>

        <b-col cols="7" v-if="false">
          <header class="d-flex mb-5 align-items-center justify-content-end">
            <h5 class="mb-0">Nuevo CP</h5>

            <div class="d-flex align-items-end ml-3">
              <BfInput
                :value="postcodeFormData.codigopostal"
                placeholder="Código Postal"
                data-key="codigopostal"
                :errors="postcodeFormErrors"
                :bottom-gutter="false"
                class="mr-2"
                @update="updatePostcodeFormField"
              />

              <BfButton @click="addNewPostcode">
                <i class="fas fa-plus" />
              </BfButton>
            </div>
          </header>

          <div v-if="postcodes.length">
            <span
              v-for="{codigopostal, id} in postcodes"
              :key="id"
              class="h3 mr-2 mb-2 d-inline-block"
            >
              <span class="badge badge-pill badge-primary">
                {{codigopostal}}
                <i
                  class="pl-1 fas fa-times fa-xs text-white postcode-badge-delete"
                  @click="deletePostcode(id)"
                />
              </span>
            </span>
          </div>
        </b-col>
      </b-row>

      <b-modal
        :visible="displayZoneForm"
        :title="zoneFormModalTitle"
        size="md"
        hide-footer
        centered
        @change="zoneFormModalChange"
      >
        <div>
          <BfInput
            label="Numero"
            data-key="numero"
            :edit-mode="isEditingZone"
            :value="zoneFormData.numero"
            :errors="zoneFormErrors"
            @update="updateZoneFormField"
          />

          <BfInput
            label="Descripcion"
            data-key="nombre"
            :edit-mode="isEditingZone"
            :value="zoneFormData.nombre"
            :errors="zoneFormErrors"
            @update="updateZoneFormField"
          />
        </div>

        <div class="mt-4 text-right">
          <BfButton
            variant="secondary"
            @click="zoneFormModalChange(false)"
          >
            Cancelar
          </BfButton>
          <BfButton
            v-if="!isEditingZone"
            class="ml-3"
            @click="saveZoneForm"
          >
            Guardar
          </BfButton>
        </div>
      </b-modal>

      <DeleteWarningModal
        :value="displayDeleteZoneWarning"
        title="¿Desea borrar esta zona?"
        @confirm-deletion="confirmZoneDeletion"
        @input="deleteZoneWarningChange"
      />

      <DeleteWarningModal
        :value="displayDeletePostcodeWarning"
        title="¿Desea borrar este código postal?"
        @confirm-deletion="confirmPostcodeDeletion"
        @input="deletePostcodeWarningChange"
      />
    </template>
  </PageWrap>
</template>

<script>
import PageWrap from '@/components/PageWrap';
import LoadingIndicator from '@/components/LoadingIndicator';
import DeleteWarningModal from '@/components/DeleteWarningModal';

import { zoneAPI } from '@/api';

export default {
  name: 'Zones',

  data: () => ({
    displayDeleteZoneWarning: false,
    displayDeletePostcodeWarning: false,
    displayZoneForm: false,
    isEditingZone: false,
    loadingData: false,
    newPostcodeValue: '',
    postcodes: [],
    postcodeFormData: {},
    postcodeFormErrors: {},
    postcodeFormPreData: {},
    postcodeIdToDelete: null,
    selectedRow: null,
    zoneFormData: {},
    zoneFormPreData: {},
    zoneFormErrors: {},
    zoneIdToDelete: null,
    zones: [],
  }),

  async created() {
    this.loadingData = true;

    try {
      this.loadZones();

      const { data: formPreData } = await zoneAPI.preCreate();
      this.zoneFormPreData = formPreData;
      this.zoneFormData = formPreData;
    } finally {
      this.loadingData = false;
    }
  },

  methods: {
    async loadZones() {
      const {
        data: { data = [] },
      } = await zoneAPI.getAll();

      this.zones = data;
    },

    async openEditZoneModal(zoneId) {
      const {
        data: [data = {}],
      } = await zoneAPI.get(zoneId);

      this.zoneFormData = data;
      this.displayZoneForm = true;
      this.isEditingZone = true;
    },

    async zoneTableSelectionChange({ newRow }) {
      if (newRow && newRow.id !== this.selectedRow) {
        this.selectedRow = newRow.id;

        this.loadZonePostcodes();
        this.loadPostcodeFormPreData();
      } else {
        this.selectedRow = null;
        this.postcodes = [];
        this.postcodeFormData = this.postcodeFormPreData;
        this.postcodeFormErrors = {};
      }
    },

    createZoneClick() {
      this.zoneFormModalChange(true);
      this.isEditingZone = false;
    },

    deleteZone(id) {
      this.displayDeleteZoneWarning = true;
      this.zoneIdToDelete = id;
    },

    zoneFormModalChange(visible) {
      this.displayZoneForm = visible;

      if (this.isEditingZone && !visible) {
        this.isEditingZone = false;
      }

      if (!visible) {
        this.zoneFormData = this.zoneFormPreData;
      }
    },

    async updateZoneFormField({ key, value }) {
      const newData = { [key]: value };

      if (this.isEditingZone) {
        this.zoneFormErrors = {};

        try {
          const { data, message } = await zoneAPI.update({
            id: this.zoneFormData.id,
            data: newData,
          });

          await this.loadZones();

          this.zoneFormData = data;

          this.$bfNotify({
            type: 'success',
            title: 'Actualizado',
            message,
          });
        } catch ({ errors, message = '' }) {
          this.zoneFormErrors = errors;

          this.$bfNotify({
            type: 'error',
            title: 'Error',
            message,
          });
        }
      } else {
        this.zoneFormData = {
          ...this.zoneFormData,
          ...newData,
        };
      }
    },

    async saveZoneForm() {
      this.zoneFormErrors = {};

      try {
        const { message } = await zoneAPI.create(this.zoneFormData);
        await this.loadZones();
        this.displayZoneForm = false;

        this.$bfNotify({
          type: 'success',
          title: 'Guardado',
          message,
        });
      } catch ({ errors, message = '' }) {
        this.zoneFormErrors = errors;

        this.$bfNotify({
          type: 'error',
          title: 'Error',
          message,
        });
      }
    },

    async confirmZoneDeletion() {
      if (this.zoneIdToDelete) {
        const { message } = await zoneAPI.delete(this.zoneIdToDelete);
        this.loadZones();
        this.displayDeleteZoneWarning = false;
        this.zoneIdToDelete = null;

        this.$bfNotify({
          type: 'success',
          title: 'Eliminado',
          message,
        });
      }
    },

    deleteZoneWarningChange(visible) {
      if (!visible) {
        this.displayDeleteZoneWarning = visible;
        this.zoneIdToDelete = null;
      }
    },

    deletePostcodeWarningChange(visible) {
      if (!visible) {
        this.displayDeletePostcodeWarning = visible;
        this.postcodeIdToDelete = null;
      }
    },

    async addNewPostcode() {
      this.postcodeFormErrors = {};

      try {
        const { message } = await zoneAPI.createPostcode({
          zoneId: this.selectedRow,
          data: this.postcodeFormData,
        });

        this.$bfNotify({
          type: 'success',
          title: 'Guardado',
          message,
        });

        this.loadZonePostcodes();
        this.postcodeFormData = {
          ...this.postcodeFormPreData,
        };
      } catch ({ errors, message }) {
        this.postcodeFormErrors = errors;

        this.$bfNotify({
          type: 'error',
          title: 'Error',
          message,
        });
      }
    },

    async loadPostcodeFormPreData() {
      const { data: formPreData } = await zoneAPI.preCreatePostcode(this.selectedRow);
      this.postcodeFormPreData = formPreData;
      this.postcodeFormData = formPreData;
    },

    async loadZonePostcodes() {
      const {
        data: { data = [] },
      } = await zoneAPI.getPostcodes(this.selectedRow);

      this.postcodes = data;
    },

    updatePostcodeFormField({ key, value }) {
      this.postcodeFormData = {
        ...this.postcodeFormData,
        [key]: value,
      };
    },

    async deletePostcode(postcodeId) {
      this.postcodeIdToDelete = postcodeId;
      this.displayDeletePostcodeWarning = true;
    },

    async confirmPostcodeDeletion() {
      const { message } = await zoneAPI.deletePostcode({
        zoneId: this.selectedRow,
        postcodeId: this.postcodeIdToDelete,
      });
      this.postcodeIdToDelete = null;
      this.displayDeletePostcodeWarning = false;
      this.loadZonePostcodes();

      this.$bfNotify({
        type: 'success',
        title: 'Eliminado',
        message,
      });
    },
  },

  computed: {
    tableColumns() {
      return [
        {
          label: 'Zona',
          dataKey: 'numero',
          sortable: true,
        },
        {
          label: 'Descripción',
          dataKey: 'nombre',
          sortable: true,
        },
        {
          label: 'Tasador de Zona',
          dataKey: 'tasador_zona_nombre',
          sortable: true,
        },
        {
          label: '',
          dataKey: 'delete',
          width: 40,
        },
      ];
    },

    zoneFormModalTitle() {
      return this.isEditingZone ? 'Editar Zona' : 'Añadir Zona';
    },
  },

  components: {
    DeleteWarningModal,
    LoadingIndicator,
    PageWrap,
  },
};
</script>

<style lang="scss" scoped>
.postcode-badge-delete {
  cursor: pointer;
}
</style>
